// @flow

import React, { type Node, type ComponentType } from 'react';
import { type PaymentMethodCode } from '@tectonics/prs/payment-method';
import { useIncomingPayment, getCountry } from '@tectonics/prs/incoming-payment';
import { type Country } from '@tectonics/prs/country';
import { DisplayCreditCardBrands, DisplayDebitCardBrands } from './display-card-brands';
import { DisplayBoletoBrand } from './display-boleto';
import { DisplayBankTransferBrand } from './display-bank-transfer';
import { DisplayPaynetBrands } from './display-paynet';
import { DisplaySpeiBrands } from './display-spei';
import { DisplayOxxoBrand } from './display-oxxo';
import { DisplayArgentineCashBrand } from './display-argentine-cash';
import { DisplayEftBrand } from './display-eft';
import { DisplayServipagBrand } from './display-servipag';
import { DisplayKlapBrand } from './display-klap';
import { DisplaySencillitoBrand } from './display-sencillito';
import { DisplayWebpayBrand } from './display-webpay';
import { DisplaySafetypayCashBrand } from './display-safetypay-cash';
import { DisplaySafetypayOnlineBrand } from './display-safetypay-online';
import { DisplayPixBrand } from './display-pix';
import { DisplayNequiBrand } from './display-nequi';
import { DisplayEfectyBrand } from './display-efecty';
import { DisplayMercadopagoBrand } from './display-mercadopago/display-mercadopago-brand';
import { DisplayAchPseBrand } from './display-externalpayprovider';

type Props = {|
  paymentMethodCode: PaymentMethodCode,
|};

export function DisplayBrands({ paymentMethodCode }: Props): Node {
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);

  const DisplayBrandsImplementation = getDisplayBrandsImplementation(paymentMethodCode, country);

  return (
    <DisplayBrandsImplementation />
  );
}

function getDisplayBrandsImplementation(paymentMethodCode: PaymentMethodCode, country: Country): ComponentType<*> {
  switch (paymentMethodCode) {
    case 'creditcard':
      return DisplayCreditCardBrands;

    case 'debitcard':
      return DisplayDebitCardBrands;

    case 'boleto':
      return DisplayBoletoBrand;

    case 'banktransfer':
      return DisplayBankTransferBrand;

    case 'cash':
      return resolveCashPaymentBrands(country);

    case 'spei':
      return DisplaySpeiBrands;

    case 'eft':
      return DisplayEftBrand;

    case 'paynet':
      return DisplayPaynetBrands;

    case 'oxxo':
    case 'oxxopay':
      return DisplayOxxoBrand;

    case 'servipag':
      return DisplayServipagBrand;

    case 'multicaja':
      return DisplayKlapBrand;

    case 'sencillito':
      return DisplaySencillitoBrand;

    case 'webpay':
      return DisplayWebpayBrand;

    case 'safetypay-cash':
      return DisplaySafetypayCashBrand;

    case 'safetypay-online':
      return DisplaySafetypayOnlineBrand;

    case 'pix':
      return DisplayPixBrand;

    case 'ewallet':
      return renderEwalletPaymentBrands(country);

    case 'externalpayprovider':
      return resolveExternalPayProviderPaymentBrands(country);

    default:
      throw new Error(`Display brands: there is no brands display for "${paymentMethodCode}".`);
  }
}

function resolveCashPaymentBrands(country: Country): ComponentType<*> {
  switch (country) {
    case 'ar':
      return DisplayArgentineCashBrand;

    case 'co':
      return DisplayEfectyBrand;

    case 'mx':
      return DisplayOxxoBrand;

    default:
      throw new Error(`PaymentMethodForm - cash - there is no cash form for country: "${country}".`);
  }
}

function renderEwalletPaymentBrands(country: Country): ComponentType<*> {
  switch (country) {
    case 'co':
      return DisplayNequiBrand;
    case 'ar':
    case 'mx':
      return DisplayMercadopagoBrand;

    default:
      throw new Error(`PaymentMethodForm - ewallet - there is no ewallet form for country: "${country}".`);
  }
}

function resolveExternalPayProviderPaymentBrands(country: Country): ComponentType<*> {
  switch (country) {

    case 'co':
      return DisplayAchPseBrand;

    default:
      throw new Error(`PaymentMethodForm - externalpayprovider - there is no externalpayprovider form for country: "${country}".`);
  }
}
